import {
  createContext, forwardRef, useCallback, useContext, useEffect, useImperativeHandle,
  useMemo, useRef, useState, type FormEvent, type ReactNode,
} from 'react';
import { Button } from './Button';

/**
 * Shape the widget sends to its host portal's submit callback. Maps 1:1 to the
 * API's SubmitFeedbackRequest (camelCase). The submitter identity is NOT sent —
 * the server derives it from the auth token (FeedbackController.CurrentUpn).
 */
export interface FeedbackSubmission {
  type: 'Bug' | 'Idea';
  category?: string | null;
  title: string;
  description: string;
  sourcePortal: string;
  sourceUrl?: string | null;
  /** Friendly screen/feature-area label — auto-detected from the route, user-overridable (#9666). */
  screenName?: string | null;
  aiAgent?: string | null;
}

/** Optional pre-fill when the widget is opened programmatically (Story F2 flag-incorrect). */
export interface FeedbackPrefill {
  type?: 'Bug' | 'Idea';
  category?: string | null;
  title?: string;
  description?: string;
  /** AI provenance — when set, the widget reports a Bug about that agent's output (BR-V-F09). */
  aiAgent?: string | null;
}

/** Imperative handle so a FeedbackProvider can open the widget pre-filled. */
export interface FeedbackWidgetHandle {
  open: (prefill?: FeedbackPrefill) => void;
}

export interface FeedbackWidgetProps {
  /** Which portal is mounting the widget — sent as sourcePortal. */
  portal: 'admin' | 'client';
  /**
   * Wires the actual API call. Must resolve to the new item's tracking
   * reference (FB-YYYY-NNNN). Each portal supplies this (apiClient.post) so
   * @babomrs/ui stays free of api-client / data-layer coupling.
   */
  submit: (input: FeedbackSubmission) => Promise<string>;
  /**
   * The "Screen / feature area" list for THIS portal (BR-CP-HOME-012) — routes in the order the select lists their
   * labels. Defaults to {@link STAFF_SCREEN_NAME_ROUTES}, today's list, so the staff portal is unchanged. The client
   * portal passes {@link CLIENT_SCREEN_NAME_ROUTES}: client and admin portals have different audiences
   * (frontend-ui-system.md), so a client is never offered a staff screen.
   */
  screens?: ReadonlyArray<ScreenNameRoute>;
}

/** One route prefix and the friendly screen/feature-area label it auto-selects. */
export interface ScreenNameRoute {
  prefix: string;
  label: string;
}

const IDEA_CATEGORIES = [
  { value: 'MakeMoney', label: 'Make money' },
  { value: 'SaveMoney', label: 'Save money' },
  { value: 'IncreaseEfficiency', label: 'Increase efficiency' },
] as const;

/**
 * "Which screen" dropdown (#9666, Epic #9653) — route → friendly screen/feature-area
 * label, covering both portals' main routes (per apps/staff-portal/src/App.tsx and
 * apps/client-portal/src/App.tsx). Auto-detected from window.location.pathname via
 * longest-prefix match (see {@link matchScreenName}); user-overridable via the
 * select below. Kept sorted longest-prefix-first isn't required — matching does a
 * full scan for the longest matching prefix regardless of declaration order.
 */
export const STAFF_SCREEN_NAME_ROUTES: ReadonlyArray<ScreenNameRoute> = [
  // ── Staff portal ──
  { prefix: '/board/', label: 'Boards / Jobs' },
  { prefix: '/reconciliation', label: 'Reconciliation' },
  { prefix: '/receipt-reconciliation', label: 'Reconciliation' },
  { prefix: '/deduct-authority', label: 'Deduct Authority' },
  { prefix: '/client-portal', label: 'Client Portal Management' },
  { prefix: '/settings/signature', label: 'My Signature' },
  { prefix: '/settings', label: 'Settings' },
  { prefix: '/client-directory', label: 'Client Directory' },
  { prefix: '/admin/jobtypes', label: 'Settings' },
  { prefix: '/admin/tasktemplates', label: 'Settings' },
  { prefix: '/admin/chasepolicies', label: 'Settings' },
  { prefix: '/admin/emailtemplates', label: 'Settings' },
  { prefix: '/admin/people', label: 'Settings' },
  { prefix: '/admin/hs-activity-mappings', label: 'Settings' },
  { prefix: '/admin/payroll-profiles', label: 'Settings' },
  { prefix: '/admin/aml-templates', label: 'AML/Compliance' },
  { prefix: '/admin/aml', label: 'AML/Compliance' },
  { prefix: '/admin/package-lookups', label: 'Settings' },
  { prefix: '/admin/document-categories', label: 'Settings' },
  { prefix: '/admin/letter-templates', label: 'Settings' },
  { prefix: '/letters/generate', label: 'Letters — Generate' },
  { prefix: '/letters/history', label: 'Letters — History' },
  { prefix: '/admin/fy-rollover', label: 'Settings' },
  { prefix: '/admin/enquiries', label: 'New Enquiries' },
  { prefix: '/ideas', label: 'Ideas & Feedback' },
  { prefix: '/admin/settings', label: 'Settings' },
  { prefix: '/resource-planning', label: 'Resource Planning' },
  { prefix: '/admin/client-groups', label: 'Client Directory' },
  { prefix: '/workspace/sign-queue', label: 'Sign Queue' },
  { prefix: '/workspace', label: 'Workspace' },
  { prefix: '/', label: 'Dashboard' },

  // ── Client portal ──
  { prefix: '/portal/onboarding', label: 'Onboarding' },
  { prefix: '/portal/signing', label: 'Signing' },
  { prefix: '/portal/queries', label: 'Queries' },
  { prefix: '/portal/documents', label: 'Documents' },
  { prefix: '/portal', label: 'Portal Home' },
  { prefix: '/about', label: 'About' },
  { prefix: '/services', label: 'Services' },
  { prefix: '/contact', label: 'Contact / Book' },
];

/**
 * BR-CP-HOME-012 — the client portal's list: client screens only, one per client destination, in the approved
 * order (cp-shell). Declared in that order because the select lists labels in declaration order; matching is
 * longest-prefix regardless. Upload and Notifications have no route yet and are still selectable. The public pages
 * stay on the list because a signed-in client may report a problem with them, though the panel is never shown
 * there; "Contact" is never "Contact / Book" (BR-CP-PUB-009).
 */
export const CLIENT_SCREEN_NAME_ROUTES: ReadonlyArray<ScreenNameRoute> = [
  { prefix: '/portal', label: 'Portal Home' },
  { prefix: '/portal/onboarding', label: 'Onboarding' },
  { prefix: '/portal/signing', label: 'Signing' },
  { prefix: '/portal/queries', label: 'Queries' },
  { prefix: '/portal/documents', label: 'Documents' },
  { prefix: '/portal/upload', label: 'Upload' },
  { prefix: '/portal/book-appointment', label: 'Book appointment' },
  { prefix: '/portal/comms-preferences', label: 'Communications' },
  { prefix: '/portal/my-details', label: 'My details' },
  { prefix: '/portal/companies', label: 'My companies' },
  { prefix: '/portal/notifications', label: 'Notifications' },
  { prefix: '/portal/help', label: 'Help centre' },
  { prefix: '/about', label: 'About' },
  { prefix: '/services', label: 'Services' },
  { prefix: '/contact', label: 'Contact' },
];

const OTHER_SCREEN = 'Other';

/**
 * Longest-prefix match of `routes` (default {@link STAFF_SCREEN_NAME_ROUTES}) against a pathname. Falls
 * back to {@link OTHER_SCREEN} when nothing matches (defensively — `/` always
 * matches, so this only triggers for a pathname that somehow isn't rooted).
 */
export function matchScreenName(
  pathname: string | null | undefined,
  routes: ReadonlyArray<ScreenNameRoute> = STAFF_SCREEN_NAME_ROUTES,
): string {
  if (!pathname) return OTHER_SCREEN;
  let best: ScreenNameRoute | null = null;
  for (const route of routes) {
    if (pathname.startsWith(route.prefix) && (!best || route.prefix.length > best.prefix.length)) {
      best = route;
    }
  }
  return best?.label ?? OTHER_SCREEN;
}

/** Options for the screen dropdown — every label in `routes` (deduped, in declaration order) + "Other" last. */
export function screenNameOptions(routes: ReadonlyArray<ScreenNameRoute> = STAFF_SCREEN_NAME_ROUTES): string[] {
  return [...Array.from(new Set(routes.map((r) => r.label))), OTHER_SCREEN];
}

const inputClasses =
  'w-full rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm ' +
  'text-[var(--text1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)] ' +
  'focus-visible:border-[var(--accent)]';

/**
 * Ideas & Feedback widget (Story D, Epic #9653; Story F2 adds the imperative
 * pre-fill). A persistent top-right launcher opens a right-side slide-out for
 * reporting a Bug or suggesting an Idea. When opened with an `aiAgent` prefill
 * (flag-incorrect), it reports a Bug about that AI agent's output (BR-V-F09).
 * Presentational only — the host portal supplies {@link FeedbackWidgetProps.submit}.
 */
export const FeedbackWidget = forwardRef<FeedbackWidgetHandle, FeedbackWidgetProps>(
  function FeedbackWidget({ portal, submit, screens = STAFF_SCREEN_NAME_ROUTES }, ref) {
    // Operator decision 2026-09-21 — the CLIENT portal submits BUGS ONLY.
    //
    // "Idea" carries the categories Make money / Save money / Increase efficiency, which are
    // internal product-prioritisation vocabulary, and ideas land on the /ideas board where staff
    // vote and comment. Asking an accounting client to triage the firm's dev backlog is the wrong
    // invitation, and a general-purpose text box on every client screen out-competes the proper
    // client-query channel — so what arrives is "my bank account is wrong", in free text, in a
    // table with none of the Tier-4 controls.
    //
    // DERIVED from `portal` rather than taken as a prop: a prop can be forgotten on a new mount,
    // and the restriction must hold for every client surface by construction. The SERVER enforces
    // the same rule from the caller's identity (FeedbackController.Submit) — this is the UI half,
    // not the control.
    const allowIdeas = portal !== 'client';
    const [open, setOpen] = useState(false);
    const [type, setType] = useState<'Bug' | 'Idea'>('Bug');
    const [category, setCategory] = useState<string>(IDEA_CATEGORIES[0].value);
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [screenName, setScreenName] = useState<string>(OTHER_SCREEN);
    const [aiAgent, setAiAgent] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [trackingRef, setTrackingRef] = useState<string | null>(null);
    const titleRef = useRef<HTMLInputElement>(null);
    const screenOptions = useMemo(() => screenNameOptions(screens), [screens]);

    // Open (optionally pre-filled). Always starts from a clean base then applies
    // the prefill, so the launcher (no prefill) opens a fresh form and a
    // flag-incorrect open (aiAgent set) opens a Bug report about that agent.
    // The screen dropdown auto-selects from the current route (#9666) — the user
    // can still override it before submitting.
    const doOpen = useCallback((prefill?: FeedbackPrefill) => {
      const agent = prefill?.aiAgent ?? null;
      // aiAgent ⇒ Bug (BR-V-F09); client portal ⇒ Bug always, even if a caller pre-fills an Idea.
      setType(agent || !allowIdeas ? 'Bug' : (prefill?.type ?? 'Bug'));
      setCategory(prefill?.category ?? IDEA_CATEGORIES[0].value);
      setTitle(prefill?.title ?? '');
      setDescription(prefill?.description ?? '');
      setScreenName(matchScreenName(typeof window !== 'undefined' ? window.location.pathname : null, screens));
      setAiAgent(agent);
      setError(null);
      setTrackingRef(null);
      setSubmitting(false);
      setOpen(true);
    }, [allowIdeas, screens]);

    useImperativeHandle(ref, () => ({ open: doOpen }), [doOpen]);

    // Close on Escape; focus the first field when opening the form.
    useEffect(() => {
      if (!open) return;
      const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
      window.addEventListener('keydown', onKey);
      if (!trackingRef) titleRef.current?.focus();
      return () => window.removeEventListener('keydown', onKey);
    }, [open, trackingRef]);

    function closePanel() {
      setOpen(false);
    }

    async function handleSubmit(e: FormEvent) {
      e.preventDefault();
      setError(null);
      if (!title.trim()) { setError('Please enter a short title.'); return; }
      if (!description.trim()) { setError('Please add a description.'); return; }

      setSubmitting(true);
      try {
        const ref2 = await submit({
          type,
          category: type === 'Idea' ? category : null,
          title: title.trim(),
          description: description.trim(),
          sourcePortal: portal,
          sourceUrl: typeof window !== 'undefined' ? window.location.pathname : null,
          screenName,
          aiAgent,
        });
        setTrackingRef(ref2);
      } catch (err) {
        setError(err instanceof Error && err.message ? err.message : 'Something went wrong — please try again.');
      } finally {
        setSubmitting(false);
      }
    }

    return (
      <>
        {open && (
          <>
            <div
              className="fixed inset-0 z-40 bg-black/30 motion-safe:animate-[fadeIn_150ms_ease-out]"
              onClick={closePanel}
              aria-hidden="true"
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Send feedback"
              className="fixed inset-y-0 right-0 z-50 flex w-full flex-col border-l border-[var(--border)]
                bg-[var(--surface)] shadow-xl sm:w-96 motion-safe:animate-[slideIn_250ms_ease-out]"
            >
              <header className="flex items-center justify-between border-b border-[var(--border)] px-5 py-4">
                <h2 className="text-lg font-medium text-[var(--text1)]">
                  {aiAgent ? 'Report incorrect AI output' : allowIdeas ? 'Send feedback' : 'Report a problem'}
                </h2>
                <button
                  type="button"
                  onClick={closePanel}
                  aria-label="Close"
                  className="inline-flex h-9 w-9 items-center justify-center rounded-md text-[var(--text2)]
                    hover:bg-[var(--background)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--accent)]"
                >
                  <CloseIcon />
                </button>
              </header>

              {trackingRef ? (
                <div className="flex flex-1 flex-col items-center justify-center gap-4 px-6 text-center">
                  <CheckIcon />
                  <div>
                    <p className="text-base font-medium text-[var(--text1)]">Thanks — we've logged it.</p>
                    <p className="mt-1 text-sm text-[var(--text2)]">
                      Your reference is{' '}
                      <span className="font-mono font-semibold text-[var(--text1)]">{trackingRef}</span>.
                    </p>
                  </div>
                  <Button variant="primary" onClick={closePanel}>Done</Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="flex flex-1 flex-col gap-4 overflow-y-auto px-5 py-4">
                  {aiAgent ? (
                    <p className="rounded-md bg-[var(--background)] px-3 py-2 text-xs text-[var(--text2)]">
                      Flagging output from the <span className="font-medium text-[var(--text1)]">{aiAgent}</span> AI agent.
                      Tell us what was wrong — a person reviews every report.
                    </p>
                  ) : !allowIdeas ? (
                    <>
                      {/* The client surface is bug-only, so there is no type control to render.
                          Say where a question about their own affairs should go instead — this
                          note is the actual fix for the channel confusion the widget creates. */}
                      <p className="rounded-md bg-[var(--background)] px-3 py-2 text-xs text-[var(--text2)]">
                        Something not working? Tell us below and a person reviews every report.
                        <br />
                        <span className="font-medium text-[var(--text1)]">
                          Have a question about your return, documents or fees?
                        </span>{' '}
                        Use <span className="font-medium text-[var(--text1)]">Queries</span> instead — it
                        reaches your accountant, and this does not.
                      </p>
                      <p className="rounded-md border border-[var(--border)] px-3 py-2 text-xs text-[var(--text2)]">
                        Please don't include your TFN, bank details or other personal information here.
                      </p>
                    </>
                  ) : (
                    <div role="radiogroup" aria-label="Feedback type" className="flex rounded-md border border-[var(--border)] p-1">
                      {(['Bug', 'Idea'] as const).map((t) => (
                        <button
                          key={t}
                          type="button"
                          role="radio"
                          aria-checked={type === t}
                          onClick={() => setType(t)}
                          className={`flex-1 rounded px-3 py-2 text-sm font-medium transition-colors ${
                            type === t ? 'bg-[var(--accent)] text-white' : 'text-[var(--text2)] hover:bg-[var(--background)]'
                          }`}
                        >
                          {t === 'Bug' ? 'Report a bug' : 'Suggest an idea'}
                        </button>
                      ))}
                    </div>
                  )}

                  <label className="flex flex-col gap-1 text-sm">
                    <span className="font-medium text-[var(--text2)]">Screen / feature area</span>
                    <select
                      value={screenName}
                      onChange={(e) => setScreenName(e.target.value)}
                      className={inputClasses}
                    >
                      {screenOptions.map((label) => (
                        <option key={label} value={label}>{label}</option>
                      ))}
                    </select>
                  </label>

                  <label className="flex flex-col gap-1 text-sm">
                    <span className="font-medium text-[var(--text2)]">Title</span>
                    <input
                      ref={titleRef}
                      type="text"
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      maxLength={200}
                      placeholder={type === 'Bug' ? 'What went wrong?' : 'What would help?'}
                      className={inputClasses}
                    />
                  </label>

                  {allowIdeas && type === 'Idea' && (
                    <label className="flex flex-col gap-1 text-sm">
                      <span className="font-medium text-[var(--text2)]">Category</span>
                      <select value={category} onChange={(e) => setCategory(e.target.value)} className={inputClasses}>
                        {IDEA_CATEGORIES.map((c) => (
                          <option key={c.value} value={c.value}>{c.label}</option>
                        ))}
                      </select>
                    </label>
                  )}

                  <label className="flex flex-1 flex-col gap-1 text-sm">
                    <span className="font-medium text-[var(--text2)]">Description</span>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={6}
                      placeholder={type === 'Bug' ? 'Steps to reproduce, what you expected…' : 'Describe your idea…'}
                      className={`${inputClasses} flex-1 resize-none`}
                    />
                  </label>

                  {error && <p role="alert" className="text-sm font-medium text-red-600">{error}</p>}

                  <Button type="submit" variant="primary" loading={submitting} className="w-full">Submit</Button>
                </form>
              )}
            </div>
          </>
        )}
      </>
    );
  },
);

// ── Provider + hook (Story F2) — lets any descendant open the widget pre-filled ──

interface FeedbackContextValue {
  /** Open the feedback panel, optionally pre-filled (e.g. flag-incorrect AI output). */
  openFeedback: (prefill?: FeedbackPrefill) => void;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

/**
 * Returns `{ openFeedback }`. Outside a {@link FeedbackProvider} it returns a
 * safe no-op so callers never need a null check (the flag-incorrect affordance
 * simply does nothing if the provider isn't mounted).
 */
export function useFeedback(): FeedbackContextValue {
  return useContext(FeedbackContext) ?? { openFeedback: () => {} };
}

/**
 * Wraps the app, renders the {@link FeedbackWidget}, and exposes
 * {@link useFeedback} so AI components can open it pre-filled. Each portal
 * supplies `portal` + `submit` (the apiClient wiring).
 */
export function FeedbackProvider({ portal, submit, screens, children }: FeedbackWidgetProps & { children: ReactNode }) {
  const widgetRef = useRef<FeedbackWidgetHandle>(null);
  const value = useMemo<FeedbackContextValue>(
    () => ({ openFeedback: (prefill) => widgetRef.current?.open(prefill) }),
    [],
  );
  return (
    <FeedbackContext.Provider value={value}>
      {children}
      <FeedbackWidget ref={widgetRef} portal={portal} submit={submit} screens={screens} />
    </FeedbackContext.Provider>
  );
}

function CloseIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
      strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" /><polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}
